import { useEffect, useState } from "react";
import { Vendor } from "../../interfaces/vendor";
import { getVendor, updateVendor } from "../../services/vendorService";
import { useParams } from "react-router-dom";
import { getStates } from "../../services/helpers";

const ViewVendor = () => {
  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState<Vendor>();
  const { vendorID } = useParams();
  const states = getStates();
  const [editing, setEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [loadingSpinner, setLoadingSpinner] = useState(false);

  useEffect(() => {
    const fetchVendorData = async () => {
      try {
        setLoading(true);
        if (vendorID) {
          const numericVendorID = parseInt(vendorID, 10);
          const vendorResponse = await getVendor(numericVendorID);
          setVendor(vendorResponse);
        }
      } catch {
        console.log("error fetching vendor");
      } finally {
        setLoading(false);
      }
    };
    fetchVendorData();
  }, [vendorID]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: keyof Vendor
  ) => {
    setEditing(true);
    let value: string | boolean =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (vendor) {
      setVendor({
        ...vendor,
        [field]: value,
      });
    }
  };

  const handleUpdateVendor = async () => {
    setLoadingSpinner(true);
    try {
      if (vendor) {
        await updateVendor(vendor);
        setEditing(false);
        setSuccessMessage("Vendor updated successfully.");
      }
    } catch (error) {
      console.error("Error updating:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <div className="content-container">
      {!loading && (
        <div className="header-container">
          <div
            className="page-items-container"
            style={{ alignItems: "center" }}
          >
            {" "}
            <h1>{vendor?.vendorName}</h1>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="data-container">
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Code:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={vendor?.code || ""}
                  onChange={(e) => handleInputChange(e, "code")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 60%" }}>
              <label className="field-label">Name:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={vendor?.vendorName || ""}
                  onChange={(e) => handleInputChange(e, "vendorName")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 50%" }}>
              <label className="field-label">Address:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={vendor?.address || ""}
                  onChange={(e) => handleInputChange(e, "address")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 40%" }}>
              <label className="field-label">Address 2:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={vendor?.address2 || ""}
                  onChange={(e) => handleInputChange(e, "address2")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">City:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={vendor?.city || ""}
                  onChange={(e) => handleInputChange(e, "city")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">State:</label>
              <select
                className={`form-select`}
                value={vendor?.state || ""}
                onChange={(e) => handleInputChange(e, "state")}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.abbreviation} value={state.abbreviation}>
                    {state.name} ({state.abbreviation})
                  </option>
                ))}
              </select>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">ZipCode:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={vendor?.zipCode || ""}
                  onChange={(e) => handleInputChange(e, "zipCode")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Phone:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={vendor?.phone || ""}
                  onChange={(e) => handleInputChange(e, "phone")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Fax:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={vendor?.fax || ""}
                  onChange={(e) => handleInputChange(e, "fax")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 50%" }}>
              <label className="field-label">Contact:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={vendor?.contact || ""}
                  onChange={(e) => handleInputChange(e, "contact")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 100%" }}>
              <label className="field-label">Comments:</label>
              <div className="field-input">
                <textarea
                  className="form-control"
                  value={vendor?.comments || ""}
                  onChange={(e) => handleInputChange(e, "comments")}
                  style={{ height: "200px" }}
                  maxLength={4000}
                />
                <div
                  style={{
                    fontSize: "14px",
                    marginTop: "4px",
                    textAlign: "right",
                  }}
                >
                  {vendor?.comments?.length ?? 0}/4000
                </div>
              </div>
            </div>
            <div className="btn-center" style={{ flex: "0 0 100%" }}>
              <button
                className={`oxbtn btn-green ${!editing ? "disabled" : ""}`}
                onClick={() => handleUpdateVendor()}
                disabled={!editing}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}
      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default ViewVendor;
